import { useCallback, useEffect, useId, useState } from 'react';
import { api } from '../../lib/api.js';

const HEADER_CHOICES = [
  ['entra', 'Entra organization name (recommended)'],
  ['company', 'Company name'],
];

const SOURCE_LABEL = { bannerLogo: 'banner logo', squareLogo: 'square logo' };
const ABSENT = 'Reports show your company name.';
// Plain words for the reason codes the collector reports (portal/shared/company-settings.mjs LOGO_REASONS).
const REASON_TEXT = {
  no_branding: 'No logo found in your Entra organizational branding.',
  fetch_failed: 'onGuard could not read your Entra organizational branding. It tries again on the next collection.',
  disabled: 'The Organization logo source is turned off in Data sources.',
  too_large: 'The logo is larger than the limit of 64 KB.',
  dimensions: 'The logo is larger than the limit of 512 by 512 pixels.',
  decompressed_too_large: 'The logo is larger than the limit of 512 by 512 pixels.',
  unsupported_type: 'The logo is not a PNG or JPEG image.',
  interlaced: 'The logo is an interlaced image. Save it as a standard PNG or JPEG.',
  progressive: 'The logo is a progressive JPEG. Save it as a standard JPEG or a PNG.',
  unavailable: 'The logo preview is not available right now.',
};
const reasonText = (reason) => REASON_TEXT[reason] ?? (reason ? 'The logo file could not be used (damaged or unsupported).' : 'onGuard has not checked for a logo yet. It does so on the next collection.');

/**
 * The detected Entra logo (ENTRAID-340 phase 3). The image is validated in the customer's own AWS account and shown from a data
 * URL: the portal never stores or logs it. Only the customer administrator can ask for the preview; an operator sees the status.
 */
function LogoPreview({ customerId, logo, canPreview }) {
  const [state, setState] = useState({ kind: logo?.available && canPreview ? 'loading' : 'absent' });
  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const out = await api.companyLogoPreview(customerId);
      setState(out?.url ? { kind: 'ready', out } : { kind: 'absent', reason: out?.reason });
    } catch {
      setState({ kind: 'error' });
    }
  }, [customerId]);
  useEffect(() => {
    if (logo?.available && canPreview) load();
    else setState({ kind: 'absent', reason: logo?.available ? null : logo?.reason, statusOnly: logo?.available === true });
  }, [logo?.available, logo?.reason, canPreview, load]);

  if (state.kind === 'loading') return <p role="status" className="text-xs text-slate-500">Checking your Entra logo...</p>;
  if (state.kind === 'error') {
    return (
      <p role="status" className="text-xs text-slate-600">
        The logo preview could not be loaded. <button type="button" className="underline" onClick={load}>Try again</button>
      </p>
    );
  }
  if (state.kind === 'ready') {
    const { out } = state;
    return (
      <div className="space-y-2" data-testid="logo-preview">
        <p className="text-xs text-slate-600">Detected: {SOURCE_LABEL[out.source] ?? 'logo'}, {out.width} x {out.height}</p>
        <img
          src={out.url}
          alt="Your Entra organizational branding logo"
          referrerPolicy="no-referrer"
          width={Math.min(out.width, 245)}
          style={{ maxWidth: '100%', height: 'auto', background: '#fff' }}
          className="rounded border border-slate-200 p-2"
          onError={() => setState({ kind: 'error' })}
        />
      </div>
    );
  }
  if (state.statusOnly) return <p className="text-xs text-slate-600">Detected: {SOURCE_LABEL[logo?.source] ?? 'logo'}. Only the customer administrator can see the preview.</p>;
  return <p className="text-xs text-slate-600">{reasonText(state.reason)} {ABSENT}</p>;
}

/**
 * Branding (ENTRAID-340). Two choices: whether reports carry the Entra logo, and which name heads the emails.
 * The customer logo is never in an email. Each change saves at once and the response replaces the view.
 *
 * @param {object} props
 * @param {string} props.customerId
 * @param {{ branding: { useEntraLogo: boolean, headerName: string }, logo?: { available: boolean, source: string|null, reason: string|null } }} props.view
 * @param {boolean} props.readOnly an operator who is not this tenant's admin
 * @param {(next: object) => void} props.onChange receives the saved view
 */
export default function BrandingTab({ customerId, view, readOnly, onChange }) {
  const groupId = useId();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const branding = view?.branding ?? { useEntraLogo: true, headerName: 'entra' };

  async function save(patch) {
    if (readOnly || busy) return;
    setBusy(true);
    setError('');
    try {
      onChange(await api.saveBranding(customerId, patch));
    } catch (e) {
      setError(typeof e?.message === 'string' && e.message ? e.message : 'The branding could not be saved. Try again in a few minutes.');
    } finally {
      setBusy(false);
    }
  }

  const disabled = readOnly || busy;
  return (
    <div className="space-y-6">
      {readOnly ? <p role="status" className="text-sm text-slate-600">You can view these settings. Only the customer administrator can change them.</p> : null}
      <section className="card space-y-3" aria-labelledby={`${groupId}-logo`}>
        <h2 id={`${groupId}-logo`}>Reports</h2>
        <div className="flex items-start gap-3 text-sm">
          <input
            id={`${groupId}-use-logo`}
            type="checkbox"
            className="mt-1"
            checked={branding.useEntraLogo}
            disabled={disabled}
            aria-describedby={`${groupId}-use-logo-note`}
            onChange={(e) => save({ useEntraLogo: e.target.checked })}
          />
          <div>
            <label htmlFor={`${groupId}-use-logo`} className="font-medium">Use my Entra logo in reports</label>
            <p id={`${groupId}-use-logo-note`} className="text-xs text-slate-500">The logo set in your Microsoft Entra organizational branding stays in your own AWS account. IPC Global never receives it.</p>
          </div>
        </div>
        <LogoPreview customerId={customerId} logo={view?.logo} canPreview={!readOnly} />
      </section>
      <section className="card space-y-3" role="radiogroup" aria-labelledby={`${groupId}-header`}>
        <h2 id={`${groupId}-header`}>Header name in emails</h2>
        {HEADER_CHOICES.map(([value, label]) => (
          <label key={value} className="flex items-center gap-3 text-sm">
            <input
              type="radio"
              name={`${groupId}-header-name`}
              checked={branding.headerName === value}
              disabled={disabled}
              onChange={() => save({ headerName: value })}
            />
            <span>{label}</span>
          </label>
        ))}
        <p className="text-xs text-slate-500">Emails show this name as text. Your logo is never placed in an email.</p>
      </section>
      {error ? <p role="alert" className="text-sm text-red-700">{error}</p> : null}
    </div>
  );
}
